import { lookupRuleOfThumb, type RuleOfThumbEntry } from "../data/rule-of-thumb-multiples";
import { REVENUE_CROSS_CHECK_PULL } from "../multiples/calculate";

export interface RuleOfThumbInput {
  industry: string;
  revenue?: number;
  sde?: number;
  inventory?: number;
  equipmentFmv?: number;
  /**
   * True when `sde` comes from source statements. The SDE rule then leads
   * and the revenue rule only pulls the range part-way toward itself
   * (`REVENUE_CROSS_CHECK_PULL`), instead of the legacy union of both rules
   * — which for an under- or over-earning business spans two ranges that
   * may not even overlap.
   */
  sdeVerified?: boolean;
}

export interface RuleOfThumbResult {
  valuationMin: number;
  valuationMax: number;
  midpoint: number;
  /** `earnings_led` (verified SDE), `union` (legacy), or which single rule applied. */
  rangeBasis: "earnings_led" | "union" | "revenue_only" | "sde_only" | "none";
  revenueBasedRange: { min: number; max: number } | null;
  sdeBasedRange: { min: number; max: number } | null;
  rule: {
    subsector: string;
    sector: string;
    revenueMultiple: { low: number; high: number } | null;
    sdeMultiple: { low: number; high: number } | null;
    notes: string;
    source: string;
  } | null;
  warnings: string[];
}

/**
 * Rule-of-thumb valuation using industry-specific benchmarks from IBBA and
 * BizBuySell market reports.
 *
 * Provides a quick sanity-check range based on how businesses in a given
 * industry have historically transacted. Not a substitute for a full
 * valuation, but useful as one data point in a multi-method approach.
 */
export function calculateRuleOfThumbValuation(
  input: RuleOfThumbInput,
): RuleOfThumbResult {
  const warnings: string[] = [];

  const entry = lookupRuleOfThumb(input.industry);
  if (!entry) {
    warnings.push(`No rule-of-thumb data found for industry "${input.industry}"`);
    return {
      valuationMin: 0,
      valuationMax: 0,
      midpoint: 0,
      rangeBasis: "none",
      revenueBasedRange: null,
      sdeBasedRange: null,
      rule: null,
      warnings,
    };
  }

  let revenueBasedRange: { min: number; max: number } | null = null;
  let sdeBasedRange: { min: number; max: number } | null = null;

  if (input.revenue && input.revenue > 0 && entry.revenueMultiple) {
    revenueBasedRange = {
      min: input.revenue * entry.revenueMultiple.low,
      max: input.revenue * entry.revenueMultiple.high,
    };
  }

  if (input.sde && input.sde > 0 && entry.sdeMultiple) {
    sdeBasedRange = {
      min: input.sde * entry.sdeMultiple.low,
      max: input.sde * entry.sdeMultiple.high,
    };
  }

  if (!revenueBasedRange && !sdeBasedRange) {
    warnings.push("Neither revenue nor SDE provided; cannot compute rule-of-thumb range");
    return {
      valuationMin: 0,
      valuationMax: 0,
      midpoint: 0,
      rangeBasis: "none",
      revenueBasedRange: null,
      sdeBasedRange: null,
      rule: formatRule(entry),
      warnings,
    };
  }

  let valuationMin: number;
  let valuationMax: number;
  let rangeBasis: RuleOfThumbResult["rangeBasis"];

  if (revenueBasedRange && sdeBasedRange) {
    if (input.sdeVerified) {
      // Earnings-led: keep the SDE rule's width, shift it part-way toward
      // the revenue rule's midpoint. Continuous whether or not the two rules
      // overlap, and collapses to the SDE rule when they agree.
      const revenueMid = (revenueBasedRange.min + revenueBasedRange.max) / 2;
      const sdeMid = (sdeBasedRange.min + sdeBasedRange.max) / 2;
      const shift = (revenueMid - sdeMid) * REVENUE_CROSS_CHECK_PULL;
      valuationMin = sdeBasedRange.min + shift;
      valuationMax = sdeBasedRange.max + shift;
      rangeBasis = "earnings_led";
    } else {
      valuationMin = Math.min(revenueBasedRange.min, sdeBasedRange.min);
      valuationMax = Math.max(revenueBasedRange.max, sdeBasedRange.max);
      rangeBasis = "union";
    }
  } else if (revenueBasedRange) {
    valuationMin = revenueBasedRange.min;
    valuationMax = revenueBasedRange.max;
    rangeBasis = "revenue_only";
  } else {
    valuationMin = sdeBasedRange!.min;
    valuationMax = sdeBasedRange!.max;
    rangeBasis = "sde_only";
  }

  const inventoryAddon = input.inventory ?? 0;
  valuationMin += inventoryAddon;
  valuationMax += inventoryAddon;

  if (inventoryAddon > 0) {
    warnings.push(
      `Inventory of $${Math.round(inventoryAddon).toLocaleString("en-US")} added to valuation range per industry convention`,
    );
  }

  // Add equipment at FMV when the industry rule specifies "plus equipment"
  const notesLower = (entry.notes ?? "").toLowerCase();
  const includesEquipment = notesLower.includes("equipment") || notesLower.includes("plus vehicles");
  const equipmentAddon = includesEquipment ? (input.equipmentFmv ?? 0) : 0;
  valuationMin += equipmentAddon;
  valuationMax += equipmentAddon;

  if (equipmentAddon > 0) {
    warnings.push(
      `Equipment at FMV of $${Math.round(equipmentAddon).toLocaleString("en-US")} added to valuation range per industry convention`,
    );
  }

  const midpoint = Math.round((valuationMin + valuationMax) / 2);

  return {
    valuationMin: Math.round(valuationMin),
    valuationMax: Math.round(valuationMax),
    midpoint,
    rangeBasis,
    revenueBasedRange: revenueBasedRange
      ? { min: Math.round(revenueBasedRange.min), max: Math.round(revenueBasedRange.max) }
      : null,
    sdeBasedRange: sdeBasedRange
      ? { min: Math.round(sdeBasedRange.min), max: Math.round(sdeBasedRange.max) }
      : null,
    rule: formatRule(entry),
    warnings,
  };
}

function formatRule(entry: RuleOfThumbEntry) {
  return {
    subsector: entry.subsector,
    sector: entry.sector,
    revenueMultiple: entry.revenueMultiple ?? null,
    sdeMultiple: entry.sdeMultiple ?? null,
    notes: entry.notes,
    source: entry.source,
  };
}
